// Native React render of the RICH Oscar order-detail block: the collapsible
// `.order-summary-details` panel (date / cancel button / print / shipping
// address / payment methods with split-pay amounts / itemized totals) plus the
// grouped, bundle-suppressed line sections with named-status progress bars,
// freight-vs-parcel tracking links, and review/register links. Ports the legacy
// order_detail_partial + order_detail_summary + order_detail_line +
// order_detail_progress_bar partials; serialized server-side by
// tsicommon.oscarapps.order.serialization_rich.
//
// The "Order Details" toggle button is markup-only by default (aria-controls +
// aria-expanded="false", no onClick): most consuming sites render the summary
// panel expanded via their own CSS and never collapse it. Sites whose CSS
// collapses the panel by default (sealy: `.order-summary-details__columns` is
// `display: none` until the header carries `--active`) pass `collapsible` so
// this component owns the disclosure state -- the button then toggles
// `order-summary-details__header--active`, its own `--order-details--close`
// arrow-flip, and aria-expanded.
import type { Immutable } from "immer";
import React from "react";
import { OrderCancelButton } from "tsi-common-react/src/apps/customer-account/OrderCancelButton/index";
import { useStaticURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import type { templates } from "@reactivated";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

type IDetail = Immutable<
    NonNullable<templates.ThankYouDocumentTemplate["rich_order_detail"]>
>;
type ISummary = IDetail["summary"];
type ILine = IDetail["shipped_events"][number]["lines"][number];

// The order block with a nullable summary: the anonymous order-status document
// carries the same three fields flattened on its page props (summary null only
// in its no-order empty states, which render no block at all).
type IProps = Immutable<{
    summary: ISummary | null;
    shipped_events: IDetail["shipped_events"];
    unshipped: IDetail["unshipped"];
    // When true, the summary panel owns its collapse/expand state (see
    // OrderSummaryDetails). Defaults to markup-only for sites that keep the
    // panel expanded via CSS.
    collapsible?: boolean;
}>;

const REGISTER_PRODUCT_URL = "https://register.tempurpedic.com/register/";

// A titled progress bar with an optional guide item. An empty title renders an
// empty div.progress-bar (no matching branch, so no title/guide).
const ProgressBar = (
    props: Immutable<{ bar: ILine["progress_bar"] }>,
): React.JSX.Element => {
    const { bar } = props;
    return (
        <div className="progress-bar">
            {bar.title && (
                <div className="progress-bar__list">
                    <h3 className="progress-bar__title">{bar.title}</h3>
                    {bar.item_modifier && (
                        <div
                            className="progress-bar__guide"
                            aria-label={t`Order Status`}
                            role="progressbar"
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={50}
                            aria-busy={false}
                        >
                            <div
                                className={`progress-bar__item progress-bar__item--${bar.item_modifier}`}
                            ></div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const LinePrice = (
    props: Immutable<{ price: NonNullable<ILine["price"]> }>,
): React.JSX.Element => {
    const { price } = props;
    return (
        <div className="product__price-container">
            {price.is_discounted ? (
                <>
                    <span className="ada-screenreader-only">
                        {t`Discounted Price:`}
                    </span>
                    <span className="product__actual-price">
                        {price.actual_display}
                    </span>
                    {price.addon_display && (
                        <>
                            {" "}
                            <span className="product__retail-price">
                                +{price.addon_display}
                            </span>
                        </>
                    )}{" "}
                    <span className="ada-screenreader-only">
                        {t`Original Price:`}
                    </span>
                    <span className="product__retail-price product__retail-price--strike">
                        {price.msrp_display}
                    </span>
                </>
            ) : (
                <>
                    <span className="product__retail-price">
                        {price.before_discounts_display}
                    </span>
                    {price.addon_display && (
                        <>
                            {" "}
                            <span className="product__retail-price">
                                +{price.addon_display}
                            </span>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

// Per-line status block: image, title, qty, price, reviews, progress bar, and
// tracking link.
const OrderLine = (props: Immutable<{ line: ILine }>): React.JSX.Element => {
    const { line } = props;
    return (
        <div className="order-detail">
            <div
                className="section__left products__results"
                role="group"
                aria-label={t`product`}
            >
                <div className="product__image">
                    <a href={line.pdp_url}>
                        {line.image_url && (
                            <img
                                className="responsive-img"
                                src={line.image_url}
                                alt={line.image_alt}
                                loading="lazy"
                                decoding="async"
                            />
                        )}
                    </a>
                </div>
                <div className="product__details">
                    <div className="product__details__copy">
                        {line.has_parent ? <h5>{line.title}</h5> : line.title}
                        <br />
                        {t`Qty:`} {line.quantity}
                        <br />
                        {!line.is_bundled && line.price && (
                            <LinePrice price={line.price} />
                        )}
                        {line.is_bundled && (
                            <div className="order-detail__bundle-callout">
                                {line.bundle_callout_text}
                            </div>
                        )}
                        <div className="product__reviews">
                            {line.reviews.has_write_review && (
                                <a
                                    href={line.reviews.write_review_url}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {t`Write a Review`}
                                </a>
                            )}
                            {line.reviews.show_divider && (
                                <>
                                    {" "}
                                    <span className="product__copy-divider">
                                        &#124;
                                    </span>{" "}
                                </>
                            )}
                            {line.reviews.show_register_link && (
                                <a
                                    href={REGISTER_PRODUCT_URL}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {t`Register Product`}
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="section__right product__status">
                <ProgressBar bar={line.progress_bar} />
                {line.show_submit_failed_copy && (
                    <div className="product__status__copy">
                        <p className="product__shipping-status">{t`Online tracking is unavailable for this order. Please contact us at 800-821-6621 if you have questions about your order.`}</p>
                    </div>
                )}
                <div className="product__status__copy">
                    {line.tracking.show_link && (
                        <div className="product__shipping-status--tracking-link-container">
                            <a
                                className={
                                    line.tracking.is_freight
                                        ? "button product__shipping-status--wg-tracking product__shipping-status--tracking-link al-shipping--wg-tracking"
                                        : "button product__shipping-status--tracking-link"
                                }
                                href={line.tracking.tracking_url}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {t`Track Shipment`}
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// The collapsible order summary panel: header (date / number / total / cancel /
// toggle) + the columns block (print, address, payment, totals). When
// `collapsible`, the panel owns its open/closed state: the header carries
// `--active` while open (the modifier the collapsing CSS keys off), the button
// its `--close` arrow-flip, and aria-expanded reflects the state. Otherwise the
// button is inert (aria-expanded="false", no onClick) and the panel stays in
// whatever state the site's CSS gives it. Closed by default so SSR matches
// hydration.
const OrderSummaryDetails = (
    props: Immutable<{ summary: ISummary; collapsible: boolean }>,
): React.JSX.Element => {
    const { summary, collapsible } = props;
    const staticURL = useStaticURL();
    const addr = summary.shipping_address;
    const [isOpen, setIsOpen] = React.useState(false);
    const open = collapsible && isOpen;
    // Per-order id so the toggle's aria-controls resolves to THIS order's panel
    // when the order-history page renders multiple RichOrderDetail instances
    // (a fixed id would duplicate across orders and AT would resolve every
    // button to the first panel). Deterministic (keyed off the order number)
    // rather than useId() so it stays SSR-stable and assertable.
    const columnsId = `order-summary-details__columns-${summary.order_number}`;
    return (
        <div className="order-summary-details details">
            <div
                className={`order-summary-details__header${
                    open ? " order-summary-details__header--active" : ""
                }`}
            >
                <div className="order-summary-details__info">
                    <h3 className="order-summary-details__order-date">
                        {summary.date_placed_display}
                    </h3>
                    <div className="order-summary-details__info-column order-summary-details__info-column--order-number">
                        {t`Order Number:`} {summary.order_number}
                    </div>
                    <div className="order-summary-details__info-column order-summary-details__info-column--total-price-wrapper">
                        {t`Total:`}{" "}
                        <span className="order-summary-details__info-column--total-price">
                            {summary.order_total_display}
                        </span>
                    </div>
                </div>
                <div className="order-summary-details__info">
                    <div className="order-summary-details__info-column order-summary-details__info-column--order-actions">
                        {summary.cancel_button && (
                            <OrderCancelButton {...summary.cancel_button} />
                        )}
                    </div>
                    <button
                        className={`order-summary-details__info-column order-summary-details__info-column--order-details${
                            open
                                ? " order-summary-details__info-column--order-details--close"
                                : ""
                        }`}
                        aria-controls={columnsId}
                        // Only expose the expand/collapse contract when this is
                        // an interactive disclosure. In the non-collapsible
                        // default the button has no onClick and the CSS leaves
                        // the panel visible, so announcing aria-expanded="false"
                        // would tell AT it's a collapsed expandable control that
                        // it can't actually operate; mark it inert instead.
                        aria-expanded={
                            collapsible ? (open ? "true" : "false") : undefined
                        }
                        aria-disabled={collapsible ? undefined : "true"}
                        onClick={
                            collapsible
                                ? (event) => {
                                      event.preventDefault();
                                      setIsOpen((v) => !v);
                                  }
                                : undefined
                        }
                    >
                        <span className="order-summary-details__order-details">
                            {t`Order Details`}
                        </span>
                    </button>
                </div>
            </div>

            <div className="order-summary-details__columns" id={columnsId}>
                <div className="order-summary-details__column--header">
                    <div
                        className="order-summary-details__print"
                        onClick={() => {
                            getWindow()?.print();
                        }}
                    >
                        <button className="order-summary-details__print-icon-container">
                            {t`Print`}
                            <img
                                className="print-icon"
                                src={`${staticURL}img/accounts/print.svg`}
                                alt=""
                            />
                        </button>
                    </div>
                </div>
                <div className="order-summary-details__column">
                    <h4 className="order-summary-details__column__title">
                        {t`Shipping Address`}
                    </h4>
                    <div className="order-summary-details__copy-block order-summary-details__address">
                        <div>{addr.name}</div>
                        <div>
                            {addr.line1}
                            {addr.line2 && <>, {addr.line2}</>}
                            {addr.line3 && <>, {addr.line3}</>}
                        </div>
                        <div>
                            {addr.city}, {addr.state} {addr.postcode}
                            <br />
                            {addr.phone_number}
                        </div>
                    </div>
                </div>

                <div className="order-summary-details__column">
                    <h4 className="order-summary-details__column__title">
                        {t`Payment Method`}
                    </h4>
                    <div className="order-summary-details__copy-block order-summary-details__payment">
                        {summary.cybersource_cards.map((card, i) => (
                            <div
                                key={`cs-${i}`}
                                className="order-summary-details__cc_payment"
                            >
                                <span className="order-summary-details__card_type">
                                    {card.card_type_name}
                                </span>{" "}
                                <span className="order-summary-details__card_mask">
                                    {t`ending in`} {card.last4}
                                </span>
                                {card.amount_display && (
                                    <span className="order-summary-details__amount">
                                        {card.amount_display}
                                    </span>
                                )}
                            </div>
                        ))}
                        {summary.financing_transfers.map((transfer, i) => (
                            <div
                                key={`fin-${i}`}
                                className="order-summary-details__financing_payment"
                            >
                                <div className="order-summary-details__account">
                                    <span>
                                        {t`Account ending in`}{" "}
                                        {transfer.masked_account_number}
                                    </span>
                                    {transfer.amount_display && (
                                        <span className="order-summary-details__amount">
                                            {transfer.amount_display}
                                        </span>
                                    )}
                                </div>
                                {transfer.disclosure && (
                                    <span className="order-summary-details__terms">
                                        {t`Terms:`} {transfer.disclosure}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="order-summary-details__breakdown order-summary-details__column">
                    <h4 className="order-summary-details__column__title">
                        {t`Order Summary`}
                    </h4>
                    <div className="order-summary-details__copy-block">
                        <div className="order-summary-details__breakdown-row">
                            <div className="order-summary-details__breakdown-title">
                                {t`Subtotal:`}
                            </div>
                            <div className="order-summary-details__breakdown-number">
                                {summary.subtotal_display}
                            </div>
                        </div>

                        <div className="order-summary-details__breakdown-row">
                            <div className="order-summary-details__breakdown-title">
                                {t`Shipping:`}
                            </div>
                            <div className="order-summary-details__breakdown-number">
                                {summary.shipping_display}
                            </div>
                        </div>

                        {summary.is_tax_known ? (
                            summary.itemized_tax_groups.map((group, i) => (
                                <div
                                    key={i}
                                    className="order-summary-details__breakdown-row"
                                >
                                    <div className="order-summary-details__breakdown-title">
                                        {group.name}
                                    </div>
                                    <div className="order-summary-details__breakdown-number">
                                        {group.amount_display}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="order-summary-details__breakdown-row">
                                <div className="order-summary-details__breakdown-title">
                                    {t`Tax:`}
                                </div>
                                <div className="order-summary-details__breakdown-number">
                                    {t`Tax will be calculated upon shipment`}
                                </div>
                            </div>
                        )}

                        <div className="order-summary-details__breakdown-row">
                            {summary.has_discount && (
                                <>
                                    <div className="order-summary-details__breakdown-title">
                                        {t`Discount:`}
                                    </div>
                                    <div className="order-summary-details__breakdown-number order-summary-details__breakdown-number--cerise">
                                        {summary.discount_display}
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="order-summary-details__breakdown-row order-summary-details__breakdown-row--total">
                            <div className="order-summary-details__breakdown-title">
                                {t`Order Total:`}
                            </div>
                            <div className="order-summary-details__breakdown-number">
                                {summary.order_total_display}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// The full rich order block: summary panel + shipped-event sections + the
// unshipped section (order_detail_partial.html).
export const RichOrderDetail = (props: IProps): React.JSX.Element => {
    return (
        <div className="order-summary">
            {props.summary && (
                <OrderSummaryDetails
                    summary={props.summary}
                    collapsible={props.collapsible ?? false}
                />
            )}

            {props.shipped_events.map((event, i) => (
                <div
                    key={`ship-${i}`}
                    className="order-summary__body order-summary__body--shipping-events-"
                >
                    <div className="section products">
                        {event.lines.map((line, j) => (
                            <OrderLine key={j} line={line} />
                        ))}
                    </div>
                </div>
            ))}

            {props.unshipped && (
                <div className="order-summary__body order-summary__body--unshipped-order">
                    <div className="section products">
                        {props.unshipped.lines.map((line, i) => (
                            <OrderLine key={i} line={line} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
